"use client";

import React, { useMemo } from "react";
import {
  OFFERING_ROLES,
  HARD_COST_CATEGORIES,
} from "@/lib/types/database/offerings";
import type { StaffLineItem, HardCostLineItem } from "@/lib/types/database/offerings";

interface OfferingLineItemsDisplayProps {
  value: StaffLineItem[] | HardCostLineItem[] | null | undefined;
  variant: "staff" | "hard-cost";
  maxItems?: number;
  className?: string;
}

export function OfferingLineItemsDisplay({
  value,
  variant,
  maxItems = 3,
  className = "",
}: OfferingLineItemsDisplayProps) {
  const summary = useMemo(() => {
    const items = Array.isArray(value) ? value : [];
    if (items.length === 0) return null;

    const slice = items.slice(0, maxItems);
    if (variant === "staff") {
      return slice
        .map((i) => {
          const s = i as StaffLineItem;
          const label = OFFERING_ROLES.find((r) => r.id === s.role)?.label ?? s.role;
          return `${label}: ${s.hours}h`;
        })
        .join(", ");
    } else {
      return slice
        .map((i) => {
          const h = i as HardCostLineItem;
          const label = HARD_COST_CATEGORIES.find((c) => c.id === h.description)?.label ?? h.description;
          return `${label}: $${h.amount}`;
        })
        .join(", ");
    }
  }, [value, variant, maxItems]);

  const extraCount = Array.isArray(value) ? Math.max(0, value.length - maxItems) : 0;

  if (!summary) {
    return <div className={`text-xs text-muted-foreground italic ${className}`}>Empty</div>;
  }

  return (
    <div className={`text-sm text-card-foreground truncate ${className}`} title={summary}>
      {summary}
      {extraCount > 0 && (
        <span className="text-muted-foreground ml-1">+{extraCount}</span>
      )}
    </div>
  );
}
